import type {
  ModerationDashboardResponseDTO,
  ModerationFlaggedContentDTO,
  ModerationMetricDTO,
  ModerationOffenderDTO,
  ModerationReportTypeCountDTO,
} from '../dashboardTypes';
import type { ModerationKpiView } from '../components/ModerationSummaryKpis';

export type ModerationCsvInput = {
  rangeLabel: string;
  days: number | null;
  startDate?: string;
  endDate?: string;
  dataSourceNote?: string;
  /** Reporter location selection (`AU` or `AU:VIC`); empty = all. */
  reportsLocation?: string;
  kpis: ModerationKpiView[];
  platformSafetyHealth?: ModerationMetricDTO[];
  moderationPipeline?: ModerationMetricDTO[];
  reportTypes?: ModerationReportTypeCountDTO[];
  reportCategoryCounts?: ModerationReportTypeCountDTO[];
  penaltyTypeCounts?: ModerationReportTypeCountDTO[];
  penaltyMetrics?: ModerationMetricDTO[];
  topOffenders?: ModerationOffenderDTO[];
  recentFlagged?: ModerationFlaggedContentDTO[];
};

function csvEscape(value: string | number | null | undefined): string {
  if (value == null) return '';
  const text = String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function section(
  title: string,
  headers: string[],
  rows: (string | number | null | undefined)[][],
): string[] {
  const lines = [csvEscape(title), headers.map(csvEscape).join(',')];
  for (const row of rows) {
    lines.push(row.map(csvEscape).join(','));
  }
  lines.push('');
  return lines;
}

function metricRows(metrics: ModerationMetricDTO[] | undefined): (string | number | null | undefined)[][] {
  return (metrics ?? []).map((m) => [
    m.key ?? '',
    m.label ?? '',
    m.value ?? '',
    m.deltaPercent ?? '',
    m.availability ?? '',
    m.note ?? '',
  ]);
}

function typeCountRows(
  rows: ModerationReportTypeCountDTO[] | undefined,
): (string | number | null | undefined)[][] {
  return (rows ?? []).map((r) => [
    r.type ?? '',
    r.label ?? '',
    r.count ?? '',
    r.percentage ?? '',
    r.deltaPercent ?? '',
  ]);
}

/** Multi-section CSV matching Engagement export style for TM-3450. */
export function buildModerationDashboardCsv(input: ModerationCsvInput): string {
  const lines: string[] = [
    ...section(
      'Filters',
      ['range', 'days', 'startDate', 'endDate', 'reportsLocation', 'dataSourceNote'],
      [
        [
          input.rangeLabel,
          input.days == null ? 'all' : input.days,
          input.startDate ?? '',
          input.endDate ?? '',
          input.reportsLocation || 'All',
          input.dataSourceNote ?? '',
        ],
      ],
    ),
    ...section(
      'Summary Metrics',
      ['key', 'label', 'value', 'deltaPercent', 'availability', 'note'],
      input.kpis.map((k) => [k.key ?? '', k.label, k.value, k.delta, k.availability ?? '', k.note ?? '']),
    ),
    ...section(
      'Platform Safety Health',
      ['key', 'label', 'value', 'deltaPercent', 'availability', 'note'],
      metricRows(input.platformSafetyHealth),
    ),
    ...section(
      'Moderation Pipeline',
      ['key', 'label', 'value', 'deltaPercent', 'availability', 'note'],
      metricRows(input.moderationPipeline),
    ),
    ...section(
      'Reports Overview (by type)',
      ['type', 'label', 'count', 'percentage', 'deltaPercent'],
      typeCountRows(input.reportTypes),
    ),
    ...section(
      'Report Categories',
      ['type', 'label', 'count', 'percentage', 'deltaPercent'],
      typeCountRows(input.reportCategoryCounts),
    ),
    ...section(
      'Penalty Management metrics',
      ['key', 'label', 'value', 'deltaPercent', 'availability', 'note'],
      metricRows(input.penaltyMetrics),
    ),
    ...section(
      'Penalty Types',
      ['type', 'label', 'count', 'percentage', 'deltaPercent'],
      typeCountRows(input.penaltyTypeCounts),
    ),
    ...section(
      'Top Offending Users',
      ['userId', 'displayName', 'username', 'violations', 'penaltyCount'],
      (input.topOffenders ?? []).map((r) => [
        r.userId ?? '',
        r.displayName ?? '',
        r.username ?? '',
        r.violations ?? '',
        r.penaltyCount ?? '',
      ]),
    ),
    ...section(
      'Recent Content Flagged',
      ['postId', 'caseId', 'state', 'riskCategory', 'contentType', 'creator', 'flaggedAt'],
      (input.recentFlagged ?? []).map((r) => [
        r.postId ?? '',
        r.caseId ?? '',
        r.state ?? '',
        r.riskCategory ?? '',
        r.contentTypeLabel ?? r.contentType ?? '',
        r.creatorDisplayName ?? '',
        r.flaggedAt ?? '',
      ]),
    ),
  ];
  return lines.join('\n');
}

export function moderationCsvFilename(days: number | null): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
  const window = days == null ? 'all' : `${days}d`;
  return `moderation-dashboard-${window}-${stamp}.csv`;
}

export function downloadTextFile(filename: string, contents: string) {
  const blob = new Blob([contents], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function csvInputFromDashboard(
  data: ModerationDashboardResponseDTO,
  kpis: ModerationKpiView[],
  rangeLabel: string,
  days: number | null,
  reportsLocation = '',
): ModerationCsvInput {
  return {
    rangeLabel,
    days,
    ...(data.startDate != null ? { startDate: String(data.startDate) } : {}),
    ...(data.endDate != null ? { endDate: String(data.endDate) } : {}),
    ...(data.dataSourceNote ? { dataSourceNote: data.dataSourceNote } : {}),
    ...(reportsLocation ? { reportsLocation } : {}),
    kpis,
    platformSafetyHealth: data.platformSafetyHealth?.metrics,
    moderationPipeline: data.moderationPipeline?.metrics,
    reportTypes: data.reportTypes,
    reportCategoryCounts: data.reportCategoryCounts,
    penaltyTypeCounts: data.penaltyTypeCounts,
    penaltyMetrics: data.penaltyManagement?.metrics,
    topOffenders: data.topOffenders,
    recentFlagged: data.recentFlagged,
  };
}
